import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * BARRACKS (Казарма) — the soldiers' MESS + BUNKS: where the off-duty garrison eats and sleeps.
 *
 * COMPOSITION — three legible zones, generous open floor between them:
 *   • HERO (west): the long communal FEAST table on its red runner, four pews set for the next meal.
 *   • SLEEP (top wall): two bunks tucked between the north doors, marked by the garrison crest + a banner.
 *   • WARM BEAT (SE corner): the mess COOKING-FIRE (one warm glow), the iron cauldron above it
 *     (engine-drawn), and a hound dozing by the coals — a single, focused fireside, not a heap of lamps.
 * A weapon rack on the west wall reads "fighting house"; a few stowed ration barrels keep it lived-in.
 * The central and south floor is left OPEN on purpose — a mess hall needs room to muster.
 *
 * Geometry: zone (40,760) 760×420 → centre (420,970). Doors kept clear — N@x200, N@x580, E@y970, S@x420.
 * Engine objects (decorate AROUND, ~40px halo): cauldron @ (700,960), barracks-settle @ (650,880).
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor: a worn runner under the feast table (laid first, under everything). ──
  place(ctx, kit.runnerRug(art, 260, 200), 260, 970);

  // ── HERO: the long communal FEAST table, west side, a pew on each end of both long sides
  //    (sit-match — the visible bench IS the sit spot: barracks-bench-1..4). ──
  place(ctx, kit.feastTable(art, 150), 260, 970);
  place(ctx, kit.pew(art, 96), 200, 900);
  place(ctx, kit.pew(art, 96), 320, 900);
  place(ctx, kit.pew(art, 96), 200, 1040);
  place(ctx, kit.pew(art, 96), 320, 1040);

  // ── SLEEP: bunks line the top wall, tucked between the two north doors (x200 / x580), with the
  //    garrison CREST on the west wall and a heraldic BANNER above them. ──
  place(ctx, kit.bunkBed(art), 140, 820);
  place(ctx, kit.bunkBed(art), 420, 820);
  place(ctx, kit.tapestry(art, 80, 110, 'crest'), 70, 880);
  place(ctx, kit.banner(art, 0x8a2f2f, 96), 420, 790);

  // ── A fighting house: the WEAPON RACK on the west wall. ──
  place(ctx, kit.weaponRack(art), 60, 1000);

  // ── WARM BEAT: the mess COOKING-FIRE (single warm pool) below the engine cauldron @ (700,960),
  //    with a tired hound curled by the coals. The settle @ (650,880) is left clear (engine sit-spot). ──
  place(ctx, kit.fireplace(art, 90), 680, 1080);
  place(ctx, kit.houndAsleep(art), 600, 1100);

  // ── Lived-in: a couple of stowed ration barrels in the SW corner, clear of the muster floor. ──
  place(ctx, kit.barrelStack(art), 120, 1130);
  place(ctx, kit.barrel(art), 200, 1140);

  // ── POOL LIGHT: one wall torch lifts the bunk side; the cooking-fire owns the SE corner alone. ──
  place(ctx, kit.wallTorch(art), 120, 790);
};

export default decorate;
